import React, { useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3, List, ListOrdered,
  Quote, Code, Link as LinkIcon, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight, Undo, Redo, Eye, EyeOff
} from 'lucide-react';

const MenuButton = ({ onClick, active, disabled, title, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded-lg transition-colors ${
      active ? 'bg-[var(--color-charcoal)] text-white' : 'text-[var(--color-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-charcoal)]'
    } ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
    data-testid={`editor-${title?.toLowerCase().replace(/\s/g, '-')}`}
  >
    {children}
  </button>
);

const ArticleEditor = ({ content, onChange, readOnly = false, fillHeight = false }) => {
  const [preview, setPreview] = React.useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-[var(--color-coral)] underline' } }),
      Image.configure({ HTMLAttributes: { class: 'rounded-lg max-w-full my-4' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: 'Start writing or paste your AI-generated content...' }),
    ],
    content: content || '',
    editable: !readOnly && !preview,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content]);

  React.useEffect(() => {
    if (editor) editor.setEditable(!readOnly && !preview);
  }, [preview, readOnly, editor]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('URL:');
    if (url) editor.chain().focus().setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Image URL:');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  if (!editor) return null;

  const ic = 'w-4 h-4';

  return (
    <div className={`border border-[var(--color-border)] rounded-xl overflow-hidden bg-white ${fillHeight ? 'flex flex-col h-full' : ''}`} data-testid="article-editor">
      {/* Toolbar */}
      {!readOnly && (
        <div className="flex items-center gap-0.5 p-2 border-b border-[var(--color-border)] bg-[var(--color-bg)] flex-wrap flex-shrink-0" data-testid="editor-toolbar">
          <MenuButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
            <Bold className={ic} />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
            <Italic className={ic} />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
            <UnderlineIcon className={ic} />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
            <Strikethrough className={ic} />
          </MenuButton>

          <div className="w-px h-5 bg-[var(--color-border)] mx-1" />

          <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1">
            <Heading1 className={ic} />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">
            <Heading2 className={ic} />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">
            <Heading3 className={ic} />
          </MenuButton>

          <div className="w-px h-5 bg-[var(--color-border)] mx-1" />

          <MenuButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">
            <List className={ic} />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered list">
            <ListOrdered className={ic} />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote">
            <Quote className={ic} />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code">
            <Code className={ic} />
          </MenuButton>

          <div className="w-px h-5 bg-[var(--color-border)] mx-1" />

          <MenuButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left">
            <AlignLeft className={ic} />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align center">
            <AlignCenter className={ic} />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right">
            <AlignRight className={ic} />
          </MenuButton>

          <div className="w-px h-5 bg-[var(--color-border)] mx-1" />

          <MenuButton onClick={addLink} active={editor.isActive('link')} title="Link">
            <LinkIcon className={ic} />
          </MenuButton>
          <MenuButton onClick={addImage} title="Image">
            <ImageIcon className={ic} />
          </MenuButton>

          <div className="flex-1" />

          <MenuButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
            <Undo className={ic} />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
            <Redo className={ic} />
          </MenuButton>

          <div className="w-px h-5 bg-[var(--color-border)] mx-1" />

          <MenuButton onClick={() => setPreview(!preview)} active={preview} title="Preview">
            {preview ? <EyeOff className={ic} /> : <Eye className={ic} />}
          </MenuButton>
        </div>
      )}

      {/* Editor Content */}
      <div className={`prose prose-sm max-w-none ${preview ? 'p-6' : 'p-4'} ${fillHeight ? 'flex-1 overflow-y-auto min-h-0' : ''}`}>
        <EditorContent editor={editor} className="min-h-[300px] focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[300px]" />
      </div>

      {/* Word count */}
      {!readOnly && (
        <div className="px-4 py-2 border-t border-[var(--color-border)] bg-[var(--color-bg)] flex items-center justify-between text-[10px] text-[var(--color-muted)] flex-shrink-0">
          <span>{editor.storage.characterCount?.words?.() || editor.getText().split(/\s+/).filter(Boolean).length} words</span>
          <span>{editor.getText().length} characters</span>
        </div>
      )}
    </div>
  );
};

export default ArticleEditor;
